// kaart van https://observablehq.com/@d3/bivariate-choropleth
// zoom van https://observablehq.com/@d3/zoom-to-bounding-box

import {geoPath, geoMercator} from 'd3-geo';
import {scaleLinear, scaleQuantile, scaleSequential, scaleQuantize, scaleSequentialQuantile} from 'd3-scale';
import {extent, range, cross} from 'd3-array';
import {zoom, zoomIdentity, zoomTransform} from 'd3-zoom';
import { select, selectAll, pointer } from 'd3-selection';
import {interpolateBlues, schemeBlues} from 'd3-scale-chromatic';
import {map} from 'd3-array';
import debounce from './debounce';

import Legend from './legend';

const d3 = {
	geoMercator,
	geoPath,
	scaleLinear,
	scaleQuantile,
	scaleQuantize,
	extent,
	range,
	cross,
	zoom,
	select,
	selectAll,
	zoomIdentity,
	zoomTransform,
	pointer,
	map,
	scaleSequential,
	interpolateBlues,
	scaleSequentialQuantile,
	schemeBlues
}


import BaseChart from './base.chart';

let chartName = 'knd-map';

// let colors =  [
//      "#e8e8e8", "#e4acac", "#c85a5a",
//       "#b0d5df", "#ad9ea5", "#985356",
//       "#64acbe", "#627f8c", "#574249"
//  ]

// pointer colors
let colors = [
	"#f1eafe", "#e9abae", "#e48079", 
	"#c6aaf9", "#bd7dab", "#b85f74", 
	"#a072f5", "#9553a9", "#673353"
]


let xObj = {
	prop: 'overgewicht_mediaan',
	label: '% overgewicht',
	cats: ['meer', 'gem.', 'minder']
}

let yObj = {
	prop: 'prct_laag_opgeleid',
	label: '% laag opgeleid',
	cats: ['minder', 'gem.', 'meer']
}

let types = {
	'mono': 'MONO',
	'bi': 'BI'
}

let labels = ["minder dan gemiddeld", "gemiddeld", "meer dan gemiddeld"]
let n = Math.floor(Math.sqrt(colors.length));
let x;
let y;

class Chart extends BaseChart {
	setup() {
		this.projection = d3.geoMercator();
		this.path = d3.geoPath();

		this.type = '';
	}

	draw() {
		this.drawSvg(chartName);
		this.drawContainer();
		this.setupPath();
	}

	drawContainer() {
		this.gContainer = this.g.append('g');

		this.gGemeente = this.gContainer.append('g')
			.attr('class', 'g-gemeente')

		this.gPaths = this.gContainer.append('g')
			.attr('class', 'g-wijken');
	} 

	updateScales() {
		this.projection
			.fitSize([this.width(), this.height()], this.data()[1]);

		this.path.projection(this.projection);
	}

	update() {
		this.updateScales();
		this.updateChart();
	}

	renderMono({
		value = () => undefined,
		range = d3.schemeBlues[3],
		scale = d3.scaleQuantize,
		title = '%',
		domain = undefined
		}) {

			this.type = types.mono;

			this.value = value;

			if(!domain) {
				const V = d3.map(this.data()[1].features, value).map(d => {
					return d == null ? NaN : +d
				});
				const V2 = d3.map(this.data()[0].features, value).map(d => {
					return d == null ? NaN : +d });

				domain = d3.extent(V);
				let domain2 = d3.extent(V2);
				// debugger;
			}

			this.colorMono = scale(domain, range);

			this.gemeente.style('fill', d => {return this.colorMono(this.value(d)) });
			// this.wijk.style('fill', d => {return color(value(d)) });

			let leg = Legend(this.colorMono, {marginLeft: 10, title: title, width: 270, tickFormat: d => {
				return Math.round(d);
			}});
		
		this.gLegend
			.attr("transform", "translate("+ 0 +"," + 0 + ")")
			.html(leg.innerHTML)

			// this.gLegend
			// 	.html(leg.innerHTML)
	}

	renderBiVariate() {
		x = d3.scaleQuantile(Array.from(this.data()[0].features, d => +d.properties[xObj.prop]), d3.range(n))
		y = d3.scaleQuantile(Array.from(this.data()[0].features, d => { 
			if(d.properties[yObj.prop] === null) return;
			return +d.properties[yObj.prop]
		}), d3.range(n));

		this.type = types.bi;

		this.gemeente.style('fill', d => this.biColor(d.properties))

		// this.wijk.style('fill', d => this.color(d.properties))

		// CREATE LEGEND
		let legendContent = this.biLegend('knd');
		const xPosLegend = this.width() < 450 ? 75 : 100;

		this.gLegend
			.attr("transform", "translate("+ xPosLegend +"," + 80 + ")")
			.html(legendContent)
			.on('click', () => this._onShowIntro());
	}

	setupPath() {
		x = d3.scaleQuantile(Array.from(this.data()[0].features, d => +d.properties[xObj.prop]), d3.range(n))
		y = d3.scaleQuantile(Array.from(this.data()[0].features, d => { 
			if(d.properties[yObj.prop] === null) return;
			return +d.properties[yObj.prop]
		}), d3.range(n));

		
		this.gemeenten =  this.gGemeente.selectAll('path')
			.data(this.data()[1].features)

		this.gemeente = this.gemeenten
			.join('path')    
				.attr('class', d => 'map-path gm-' + d.properties.GM_CODE)
				.on("click", (event, d) => this.clicked(event, d))
				.on("mouseover", (event, d) => this.onSelected(event,d))
				.on("mouseout", (event, d) => this._onMouseOut());

		this.wijken = this.gPaths.selectAll('path')
			.data(this.data()[0].features)

		this.wijk = this.wijken
			.join('path')    
				.attr('class', d => `map-path map-buurt gm-${d.properties.GM_CODE} wk-${d.properties.WK_CODE}` )
				.on("mouseover", (event, d) => this.onSelected(event, d))
				.on("mouseout", (event, d) => this._onMouseOut());

		// SET ZOOM
		this.zoom = d3.zoom()
			.scaleExtent([1, 11])
			.filter((event) => {

				if (event.type === 'wheel') {
					if (event.shiftKey && this.onShowOverlay()) {
						this._onShowOverlay(false);
						return true;
					} else if(this.onShowOverlay()){
						this._onShowOverlay(true);
						return false;
					}					
				}

				if(event.type === 'touchstart') {
					if(event.touches.length > 1) {
						// this._onShowOverlay(false);
						return true;
					} else {
						// this._onShowOverlay(true);
						return false;
					}
				}

				return false;
			})
			.on('zoom', (event) => {				
				this.zoomed(event)
			})
			.on("end", (event) => this.zoomEndFunction(event));

		this.svg.call(this.zoom)
	}

	onSelected(event, item) {
		let d = item.properties;

		let xValue = +d[xObj.prop];
		let yValue = +d[yObj.prop];

		d.woningcorperatiebezitLabel = labels[x(xValue)];
		d.energiearmoedeLabel = labels[y(yValue)];

		let container = d3.select('.container-map');
		const [mouseX, mouseY] = d3.pointer(event, container.node());

		this._onMouseOver(d, mouseX, mouseY);
	}

	zoomEndFunction(event) {
		if (event.transform.k < 6) {
			this.gPaths.classed('highlight-active', false);
		}

		let showResetBtn = event.transform.k > 1.5 ? true : false;
		if(this.onShowReset) {
			this._onShowReset(showResetBtn);
		}

		let show = event.transform.k > 6;
		
		this.gContainer.classed('show-wijken', show);
	}

	clicked(event, d) {
		const [[x0, y0], [x1, y1]] = this.path.bounds(d);
		if(event) {
			event.stopPropagation();
			this._onResetSearch();
		}

		this.gPaths.classed('highlight-active', true);
		this.wijk.classed('active', false);
		d3.selectAll('.map-buurt.gm-' + d.properties.GM_CODE).classed('active', true);

		this.wijk
			.filter(wijk => { return wijk.properties.GM_CODE ===  d.properties.GM_CODE})
			.attr('d', this.path);

			// debugger;

		if(this.type === types.mono) {
			d3.selectAll('.map-path.active')
				.style('fill', d => { return this.colorMono(this.value(d)) });			
		} else if (this.type === types.bi) {
			d3.selectAll('.map-path.active')
				.style('fill', d => {return this.biColor(d.properties) });			
		}

		this.svg.transition().duration(1500).call(
			this.zoom.transform,
			d3.zoomIdentity
				.translate(this.width() / 2, this.height() / 2)
				.scale(Math.min(11, 0.9 / Math.max((x1 - x0) / this.width(), (y1 - y0) / this.height())))
				.translate(-(x0 + x1) / 2, -(y0 + y1) / 2)
			// d3.pointer(event, this.svg.node())
		);
	}

	zoomIn(gmCode) {
		let d = d3.select('.gm-' + gmCode);
		this.clicked(null, d.datum());

		this._onMouseOver(d.datum().properties);
	}

	zoomed(event) {
		const {transform} = event;
		
		this.gContainer.attr("transform", transform);
	}

	resetZoom() {
			this.svg.transition().duration(1500).call(
				this.zoom.transform,
				d3.zoomIdentity,
				d3.zoomTransform(this.svg.node()).invert([this.width() / 2, this.height() / 2])
			);
	}

	biLegend(id) {

			const k = this.width() < 450 ? 20 : 30;
			const arrow = id;
			let titel1 = xObj.label;
			let titel2 = yObj.label;
			const cats1 = xObj.cats;
			const cats2 = yObj.cats;

				// <g class='knd-legend-uitleg' transform="translate(${k},-40) rotate(45)">
				// 	<circle cx="10.77" cy="10.77" r="10.77"/>
				// 	<text class="cls-1" transform="translate(8.35 14.5)">?</text>
				// </g>
			return `<g font-family=sans-serif font-size=10>
				<g transform="translate(-${k * n / 2},-${k * n / 2}) rotate(-45 ${k * n / 2},${k * n / 2})">
					
					<marker id="${arrow}" markerHeight=10 markerWidth=10 refX=6 refY=3 orient=auto>
						<path d="M0,0L9,3L0,6Z" />
					</marker>

					${d3.cross(d3.range(n), d3.range(n)).map(([i, j]) => `
						<rect width=${k} height=${k} x=${i * k} y=${(n - 1 - j) * k} fill=${colors[j * n + i]}>
							<title>${titel1}${labels[j] && ` (${labels[j]})`}
							${titel2}${labels[i] && ` (${labels[i]})`}</title>
						</rect>`
					)}

					${d3.cross(d3.range(n)).map(([i, j]) => `
						<line marker-end="url(#${arrow})" x1=-20 x2=-5 y1=${(i * k) + (k / 2)} y2=${(i * k) + (k / 2)} stroke=white stroke-width=1.5 />
						<text font-weight="bold" dy="0.9em" transform="rotate(90) translate(${(i * k) + (k / 2)},24)" text-anchor="middle">${cats1[i]}</text>

						<line marker-end="url(#${arrow})" x1=${(i * k) + (k / 2)} x2=${(i * k) + (k / 2)} y1=${(n * k) + 20} y2=${(n * k) + 5} stroke=white stroke-width=1.5 />
						<text font-weight="bold" dy="0.9em" transform="rotate(0) translate(${(i * k) + (k / 2)},${n * k + 24})" text-anchor="middle">${cats2[i]}</text>`
					)}
					

					<text font-weight="bold" dy="0.9em" transform="rotate(90) translate(${n / 2 * k},50)" text-anchor="middle">${titel1}</text>
					<text font-weight="bold" dy="0.71em" transform="translate(${n / 2 * k},${n * k + 50})" text-anchor="middle">${titel2}</text>
				</g>
			</g>`;
	}

	// <line marker-end="url(#${arrow})" x1=0 x2=${n * k} y1=${n * k} y2=${n * k} stroke=white stroke-width=1.5 />
	// <line marker-end="url(#${arrow})" y2=0 y1=${n * k} stroke=white stroke-width=1.5 />
	// <text font-weight="bold" dy="0.9em" transform="rotate(90) translate(${n / 2 * k},6)" text-anchor="middle">${titel1}</text>
	// <text font-weight="bold" dy="0.71em" transform="translate(${n / 2 * k},${n * k + 6})" text-anchor="middle">${titel2}</text>

	biColor(value) {
		if (value[xObj.prop] === null || value[yObj.prop] === null) return "#07034A";

		let xValue = +value[xObj.prop];
		let yValue = +value[yObj.prop];
		return colors[y(yValue) + x(xValue) * n];
	}

	updateChart() {
		// this.wijk
		// 	.attr('d', this.path);

		this.gemeente
			.attr('d', this.path);
	}

	highlight(className) {
		d3.selectAll(`.${className}`).raise().classed('highlight', true);
	}

	resetHighlight(){
		d3.selectAll('.highlight').classed('highlight', false);
	}

	render() {
		this.setResponsive();

		this.setup();
		this.draw();
		this.update();

		return this;
	}

	resizeChart() {
		this.update();
	}

	onResetSearch(cb) {
		if (!arguments.length) return this._onResetSearch;
		this._onResetSearch = cb;
		return this;	
	}

	onShowOverlay(cb) {
		if (!arguments.length) return this._onShowOverlay;
		this._onShowOverlay = cb;
		return this;
	}

	onShowIntro(cb) {
		if (!arguments.length) return this._onShowIntro;
		this._onShowIntro = cb;
		return this;
	}

	onShowReset(cb) {
		if (!arguments.length) return this._onShowReset;
		this._onShowReset = cb;
		return this;
	}

	onMouseOver(cb) {
		if (!arguments.length) return this._onMouseOver;
		this._onMouseOver = cb;
		return this;
	}

	onMouseOut(cb) {
		if (!arguments.length) return this._onMouseOut;
		this._onMouseOut = cb;
		return this;
	}			
}

export default Chart;